import React, { useState, useEffect, useCallback } from 'react';
import { naytiveAPI } from '../services/api';

/* Filing-template editor (Phase 5) — CRUD over filing_templates + their
 * applicability rules. This is where "what compliance comes next" is authored:
 * the same templates the reminder engine + user calendar read. */

const CATEGORIES = ['federal_tax', 'state_tax', 'info_return', 'franchise', 'annual_report', 'vat_gst', 'payroll', 'foreign_disclosure'];
const FREQ = ['annual', 'quarterly', 'monthly', 'one_time', 'biennial'];
const SEV = ['critical', 'high', 'medium', 'low'];
const APPLIES = ['entity', 'individual', 'both'];

const DUE_RULE_HINT = '{"kind":"fixed_date","month":4,"day":15} · {"kind":"after_fy_end","days":105} · {"kind":"quarterly_offset","month_in_q":2,"day":28} · {"kind":"monthly_offset","day":20} · {"kind":"n_days_after_incorp","days":30}';
const COND_HINT = '{"all":[{"fact":"entity.legal_type","operator":"in","value":["delaware_llc"]}]}';

function parseJsonOr(s, fallback) {
  try { return JSON.parse(s); } catch { return fallback; }
}

export default function FilingTemplates() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selId, setSelId] = useState(null);
  const [toast, setToast] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await naytiveAPI.listFilingTemplates();
      setList(r.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const byCountry = list.reduce((acc, t) => {
    (acc[t.country_code || '—'] = acc[t.country_code || '—'] || []).push(t);
    return acc;
  }, {});

  return (
    <div style={{ padding: 28, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Filing Templates</h1>
          <p style={{ color: '#6B7280', fontSize: 13, margin: '4px 0 0' }}>
            The editable compliance catalog — reminders and the founder calendar read these.
          </p>
        </div>
        <button className="btn-primary" onClick={() => { setCreating(true); setSelId(null); }}>+ New template</button>
      </div>

      {toast && (
        <div style={{ marginBottom: 12, padding: '9px 14px', background: '#ECFDF5', color: '#047857', borderRadius: 8, fontSize: 13 }}>{toast}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 16, color: '#6B7280', fontSize: 13 }}>Loading…</div>
          ) : Object.keys(byCountry).sort().map((cc) => (
            <div key={cc}>
              <div style={{ padding: '8px 14px', background: '#F9FAFB', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#9CA3AF' }}>{cc}</div>
              {byCountry[cc].map((t) => (
                <button key={t.id} onClick={() => { setSelId(t.id); setCreating(false); }} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', textAlign: 'left',
                  padding: '10px 14px', border: 'none', borderTop: '1px solid #F3F4F6', cursor: 'pointer',
                  background: selId === t.id ? '#EEF2FF' : '#fff',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{t.name}</span>
                  <span style={{ fontSize: 10.5, color: '#9CA3AF', fontFamily: 'monospace' }}>{t.code}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        <div>
          {creating ? (
            <NewTemplateForm onCancel={() => setCreating(false)} onCreated={async (id) => { setCreating(false); await loadList(); setSelId(id); setToast('Template created'); }} />
          ) : selId ? (
            <TemplateEditor key={selId} id={selId} onSaved={async () => { await loadList(); setToast('Saved'); }} />
          ) : (
            <div style={{ border: '1px dashed #E5E7EB', borderRadius: 12, padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              Select a template to edit, or create a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11.5, fontWeight: 600, color: '#6B7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</label>
      {children}
    </div>
  );
}
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' };

function TemplateEditor({ id, onSaved }) {
  const [tpl, setTpl] = useState(null);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [dueRuleStr, setDueRuleStr] = useState('');
  const [newCond, setNewCond] = useState('');

  const load = useCallback(async () => {
    const r = await naytiveAPI.getFilingTemplate(id);
    const it = r.data?.item;
    setTpl(it);
    setRules(r.data?.rules || []);
    const dr = typeof it.due_rule === 'string' ? it.due_rule : JSON.stringify(it.due_rule);
    setDueRuleStr(dr);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!tpl) return <div style={{ padding: 20, color: '#6B7280' }}>Loading…</div>;

  const save = async () => {
    setSaving(true); setErr(null);
    const due = parseJsonOr(dueRuleStr, null);
    if (!due) { setErr('due_rule is not valid JSON'); setSaving(false); return; }
    try {
      await naytiveAPI.updateFilingTemplate(id, {
        name: tpl.name, tldr: tpl.tldr, description: tpl.description,
        severity: tpl.severity, category: tpl.category, frequency: tpl.frequency,
        applies_to: tpl.applies_to, is_active: !!tpl.is_active, due_rule: due,
        external_link: tpl.external_link,
      });
      await onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const addRule = async () => {
    const cond = parseJsonOr(newCond, null);
    if (!cond) { setErr('condition is not valid JSON'); return; }
    await naytiveAPI.addFilingTemplateRule(id, { condition_json: cond });
    setNewCond('');
    await load();
  };
  const delRule = async (ruleId) => {
    await naytiveAPI.deleteFilingTemplateRule(id, ruleId);
    await load();
  };

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#6B7280', background: '#F3F4F6', padding: '2px 8px', borderRadius: 6 }}>{tpl.code}</span>
        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={!!tpl.is_active} onChange={(e) => setTpl({ ...tpl, is_active: e.target.checked })} /> Active
        </label>
      </div>
      <Field label="Name"><input style={inputStyle} value={tpl.name || ''} onChange={(e) => setTpl({ ...tpl, name: e.target.value })} /></Field>
      <Field label="Plain-English summary (tldr)"><textarea style={{ ...inputStyle, minHeight: 54 }} value={tpl.tldr || ''} onChange={(e) => setTpl({ ...tpl, tldr: e.target.value })} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Severity"><select style={inputStyle} value={tpl.severity} onChange={(e) => setTpl({ ...tpl, severity: e.target.value })}>{SEV.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Frequency"><select style={inputStyle} value={tpl.frequency} onChange={(e) => setTpl({ ...tpl, frequency: e.target.value })}>{FREQ.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Category"><select style={inputStyle} value={tpl.category} onChange={(e) => setTpl({ ...tpl, category: e.target.value })}>{CATEGORIES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Applies to"><select style={inputStyle} value={tpl.applies_to} onChange={(e) => setTpl({ ...tpl, applies_to: e.target.value })}>{APPLIES.map((s) => <option key={s}>{s}</option>)}</select></Field>
      </div>
      <Field label="Due rule (JSON)">
        <textarea style={{ ...inputStyle, minHeight: 44, fontFamily: 'monospace', fontSize: 12 }} value={dueRuleStr} onChange={(e) => setDueRuleStr(e.target.value)} />
        <div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 3 }}>{DUE_RULE_HINT}</div>
      </Field>

      {err && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <button className="btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save template'}</button>

      <div style={{ marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 10 }}>APPLICABILITY RULES ({rules.length})</div>
        {rules.map((r) => (
          <div key={r.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
            <pre style={{ flex: 1, margin: 0, fontSize: 11, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: 8, overflowX: 'auto' }}>{typeof r.condition_json === 'string' ? r.condition_json : JSON.stringify(r.condition_json)}</pre>
            <button className="btn-cancel" onClick={() => delRule(r.id)} style={{ padding: '4px 10px' }}>×</button>
          </div>
        ))}
        <textarea style={{ ...inputStyle, minHeight: 44, fontFamily: 'monospace', fontSize: 12, marginTop: 6 }} placeholder={COND_HINT} value={newCond} onChange={(e) => setNewCond(e.target.value)} />
        <button className="btn-cancel" onClick={addRule} style={{ marginTop: 6 }}>+ Add rule</button>
      </div>
    </div>
  );
}

function NewTemplateForm({ onCancel, onCreated }) {
  const [f, setF] = useState({ code: '', name: '', jurisdiction_country_id: '', applies_to: 'entity', category: 'federal_tax', frequency: 'annual', severity: 'medium', due_rule: '{"kind":"fixed_date","month":4,"day":15}' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const due = parseJsonOr(f.due_rule, null);
    if (!f.code || !f.name) { setErr('code and name required'); return; }
    if (!f.jurisdiction_country_id) { setErr('jurisdiction_country_id required (numeric countries.id)'); return; }
    if (!due) { setErr('due_rule not valid JSON'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await naytiveAPI.createFilingTemplate({ ...f, jurisdiction_country_id: Number(f.jurisdiction_country_id), due_rule: due });
      await onCreated(r.data?.id);
    } catch (e) {
      setErr(e.response?.data?.message || 'Create failed');
    } finally { setBusy(false); }
  };
  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, padding: 20 }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>New filing template</h3>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Code"><input style={inputStyle} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="WY_ANNUAL_REPORT" /></Field>
        <Field label="Country id (countries.id)"><input style={inputStyle} value={f.jurisdiction_country_id} onChange={(e) => setF({ ...f, jurisdiction_country_id: e.target.value })} placeholder="130 = US" /></Field>
      </div>
      <Field label="Name"><input style={inputStyle} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Applies to"><select style={inputStyle} value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value })}>{APPLIES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Severity"><select style={inputStyle} value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}>{SEV.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Category"><select style={inputStyle} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{CATEGORIES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Frequency"><select style={inputStyle} value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}>{FREQ.map((s) => <option key={s}>{s}</option>)}</select></Field>
      </div>
      <Field label="Due rule (JSON)"><textarea style={{ ...inputStyle, minHeight: 44, fontFamily: 'monospace', fontSize: 12 }} value={f.due_rule} onChange={(e) => setF({ ...f, due_rule: e.target.value })} /><div style={{ fontSize: 10.5, color: '#9CA3AF', marginTop: 3 }}>{DUE_RULE_HINT}</div></Field>
      {err && <div style={{ color: '#DC2626', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" disabled={busy} onClick={submit}>{busy ? 'Creating…' : 'Create template'}</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
